import type { ClassValue } from "clsx"
import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

import type { NumberFormatOptions } from "@/lib/formatters"
import { numberFormatter } from "@/lib/formatters"

const DEFAULT_PRICE_FORMAT_OPTIONS: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  style: "currency",
  currency: "EUR",
}

type DateBoundary = "start" | "end"

type DateOffset = {
  year?: number
  month?: number
  day?: number
}

function isDate(value: unknown): value is Date {
  return value instanceof Date
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function download(blob: Blob, filename: string) {
  const link = document.createElement("a")
  link.href = URL.createObjectURL(blob)
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(link.href)
}

export const formatPrice = (
  value: number | string,
  options: Intl.NumberFormatOptions = {},
  locale = "es-ES",
): string => {
  const num = typeof value === "string" ? Number(value) : value

  return new Intl.NumberFormat(locale, {
    ...DEFAULT_PRICE_FORMAT_OPTIONS,
    ...options,
  }).format(isNaN(num) ? 0 : num)
}

export function getDateWithOffset(
  baseDate: Date,
  offset?: DateOffset,
  boundary?: DateBoundary,
): Date
export function getDateWithOffset(
  offset?: DateOffset,
  boundary?: DateBoundary,
): Date
export function getDateWithOffset(
  baseDateOrOffset: Date | DateOffset = new Date(),
  offsetOrBoundary: DateOffset | DateBoundary = {},
  boundary: DateBoundary = "start",
): Date {
  const baseDate = isDate(baseDateOrOffset) ? baseDateOrOffset : new Date()
  const offset = isDate(baseDateOrOffset)
    ? typeof offsetOrBoundary === "string"
      ? {}
      : offsetOrBoundary
    : baseDateOrOffset
  const resolvedBoundary = isDate(baseDateOrOffset)
    ? typeof offsetOrBoundary === "string"
      ? offsetOrBoundary
      : boundary
    : typeof offsetOrBoundary === "string"
      ? offsetOrBoundary
      : boundary

  const nextDate = new Date(baseDate)

  nextDate.setFullYear(nextDate.getFullYear() + (offset.year ?? 0))
  nextDate.setMonth(nextDate.getMonth() + (offset.month ?? 0))
  nextDate.setDate(nextDate.getDate() + (offset.day ?? 0))

  if (resolvedBoundary === "end") {
    nextDate.setHours(23, 59, 59, 999)
  } else {
    nextDate.setHours(0, 0, 0, 0)
  }
  return nextDate
}
